import styled, { css } from 'styled-components'

import { SpokeDeliveryStatus } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import {
  _S_Normal,
  _XS_Bold,
  _XS_Normal,
  mainColors,
  semanticColors,
} from '@avantstay/backoffice-core'

export type BubbleVariant = 'in' | 'out' | 'ai' | 'note'

export const Row = styled.div<{ $isInbound: boolean; $isRunEnd: boolean }>`
  display: grid;
  grid-template-columns: ${({ $isInbound }) => {
    return $isInbound ? '28px minmax(0, 1fr)' : 'minmax(0, 1fr) 28px'
  }};
  column-gap: 8px;
  margin-bottom: ${({ $isRunEnd }) => ($isRunEnd ? '16px' : '4px')};
`

export const AvatarSlot = styled.span`
  width: 28px;
  flex: none;
`

export const Column = styled.span<{ $isInbound: boolean }>`
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: ${({ $isInbound }) => ($isInbound ? 'flex-start' : 'flex-end')};
`

export const Header = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 4px;
`

export const Name = styled.span<{ $isAi?: boolean }>`
  ${_XS_Bold};
  color: ${({ $isAi }) => ($isAi ? mainColors.accentDark : mainColors.primaryMedium)};
  overflow-wrap: anywhere;
`

const bubbleVariants = {
  in: css`
    background: ${mainColors.white};
    border: 1px solid ${mainColors.neutralMedium};
    color: ${mainColors.primaryMedium};
  `,
  out: css`
    background: ${mainColors.accentExtraLight};
    border: 1px solid transparent;
    color: ${mainColors.primaryMedium};
  `,
  ai: css`
    background: ${mainColors.accentExtremelyLight};
    border: 1px dashed ${mainColors.accentLightMedium};
    color: ${mainColors.primaryMedium};
  `,
  note: css`
    background: ${semanticColors.warningExtraLight};
    border: 1px solid ${semanticColors.warningLight};
    color: ${mainColors.primaryMedium};

    a {
      color: ${mainColors.accentMedium};
    }
  `,
}

const bubbleCorners = ($variant: BubbleVariant, $isRunStart: boolean) => {
  if (!$isRunStart) return '12px'

  return $variant === 'in' ? '4px 12px 12px 12px' : '12px 4px 12px 12px'
}

const deliveryBorder: Record<SpokeDeliveryStatus, string> = {
  [SpokeDeliveryStatus.pending]: `1px dashed ${mainColors.accentLightMedium}`,
  [SpokeDeliveryStatus.failed]: `1px solid ${semanticColors.failureMedium}`,
  [SpokeDeliveryStatus.sent]: `none`,
}

interface BubbleProps {
  $variant: BubbleVariant
  $isRunStart: boolean
  $deliveryStatus?: SpokeDeliveryStatus | null
  $isPending?: boolean
}

export const Bubble = styled.span<BubbleProps>`
  ${_S_Normal};
  max-width: 52ch;
  padding: 8px 12px;
  line-height: 1.55;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  border: ${({ $deliveryStatus }) => ($deliveryStatus ? deliveryBorder[$deliveryStatus] : null)};
  border-radius: ${({ $variant, $isRunStart }) => bubbleCorners($variant, $isRunStart)};
  opacity: ${({ $isPending }) => ($isPending ? 0.5 : 1)};

  ${({ $variant }) => bubbleVariants[$variant]};
`

export const When = styled.span`
  ${_XS_Normal};
  color: ${mainColors.neutralExtremelyDark};
  margin-top: 4px;
`
